<template>
    <div class="checkin-cell" :class="{ 'open-up': shouldDropdownOpenUp }" @click.stop @mouseleave="onMouseLeave ">
        <div class="display" :class="{ disabled: isDisabled, bold: !(checkinInfo.isCancelled || isPendingOrDue) }" @click.stop="toggle">
            <span v-if="checkinInfo.isCancelled || isPendingOrDue">- | -</span>
            <span v-else-if="checkinInfo.checked === 0 && checkinInfo.noShow === 0">Not checked in</span>
            <span v-else>{{ checkinInfo.checked }} | {{ checkinInfo.noShow }}</span>
            <span v-if="!isDisabled" class="dropdown-arrow" :class="{ open: open }">▾</span>
            <loader v-else-if="loading" size="small" />
        </div>
        <ul v-if="open" class="menu" :class="{ bold: !(checkinInfo.isCancelled || isPendingOrDue) }" @click.stop>
            <li :class="{ disabled: loading || !isCheckedEnabled }" @click.stop="!loading && isCheckedEnabled && select(checkinActions.CHECKED)">Checked in</li>
            <li :class="{ disabled: loading || !isNoShowEnabled }" @click.stop="!loading && isNoShowEnabled && select(checkinActions.NOSHOW)">No-show</li>
            <li :class="{ disabled: loading }" @click.stop="!loading && select(checkinActions.PER_CLIENT)">Check-in per client</li>
        </ul>
    </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import { RESERVATION_STATUS } from '@/enums/reservationStatus'
import { PAYMENT_STATUS } from '@/enums/paymentStatus'
import { CHECKIN_ACTIONS } from '@/enums/checkinActions'
import reservationService from '@/api/reservation/reservationService'
import Loader from '@/tools/loaders/loader1'
import dropdownDirectionHelper from '@/utils/dropdownDirectionHelper'

export default {
    name: 'CheckinDropdown',
    props: { arguments: { type: Object, required: true } },
    data() { 
        return { open: false, shouldDropdownOpenUp: false, checkinActions: CHECKIN_ACTIONS, loading: false } 
    },
    computed: {
        ...mapGetters(['account', 'getSelectedReservation']),
        reservation() { return this.arguments.reservation || this.arguments },
        isPendingOrDue() { return this.reservation.status === RESERVATION_STATUS.PENDING || this.reservation.paymentStatus === PAYMENT_STATUS.IN_DUE },
        checkinInfo() { 
            return (this.arguments && this.arguments.checkinInfo) ? this.arguments.checkinInfo : {
                checked: this.reservation.paxCheckedIn || 0,
                checkedQuantity: this.reservation.paxCheckedIn || 0,
                noShow: this.reservation.paxNoShow || 0,
                noShowQuantity: this.reservation.paxNoShow || 0,
                total: this.reservation.paxTotal || 0,
                totalPax: this.reservation.paxTotal || 0,
                isCancelled: this.reservation.status === RESERVATION_STATUS.CANCELED,
                isDisabled: false,
                display: null
        }},
        isDisabled() { 
            return this.checkinInfo.isDisabled || this.checkinInfo.isCancelled || this.isPendingOrDue || this.loading
        },
        isCheckedEnabled() {
            return !this.isDisabled && this.checkinInfo.total > 0 && this.checkinInfo.checked < this.checkinInfo.total;
        },
        isNoShowEnabled() {
            return !this.isDisabled && this.checkinInfo.total > 0 && this.checkinInfo.noShow < this.checkinInfo.total;
        },
        
    },
    methods: {
        ...mapActions(['updateExistingReservation','openModal','setSelectedReservation']),
        toggle() { 
            if (!this.isDisabled) {
                if (!this.open) this.$emit('closeAllDropdowns', this.dropdownUid);

                if (this.open) {
                    dropdownDirectionHelper.closeDropdown.call(this);
                    return
                }

                dropdownDirectionHelper.openDropdown.call(this);
            }
        },
        async select(selectedOption) {
            if (this.isDisabled) return;

                this.loading = true;
                this.open = false;
                this.shouldDropdownOpenUp = false;
                this.$emit('close');

            

            try {
                const res = await reservationService.openReservationById(this.account.supplierId, this.reservation.id);
                if (!res.ok) {
                    this.open = false;
                    return;
                }

                const reservationContext = structuredClone(res.value);

                if (selectedOption === this.checkinActions.PER_CLIENT) {
                    this.setSelectedReservation({ payload: reservationContext, isToResetFlags: true });
                    this.openModal('ReservationEditV2');
                    return;
                }

                reservationContext.paxCheckedIn = (selectedOption === this.checkinActions.CHECKED) ? this.checkinInfo.total : 0;
                reservationContext.paxNoShow = (selectedOption === this.checkinActions.NOSHOW) ? this.checkinInfo.total : 0;

                if (reservationContext.updateFlags) reservationContext.updateFlags.checkins = true;
                await this.updateExistingReservation({ supplierId: this.account.supplierId, reservation: reservationContext });
                this.$emit('updateReservationsList', reservationContext);

            } catch (err) {
            } finally {
                this.open = false;
                this.loading = false;
            }
        },
        onMouseLeave () {
            if (this.open) {
                this.open = false;
                this.shouldDropdownOpenUp = false;
                this.$emit('close');
            }
        }
    },
    components: { Loader }
}
</script>

<style scoped>
    .checkin-cell {
        position: relative;
        display: inline-block;
    }

    .display {
        padding: 0;
        border: none;
        border-radius: 0;
        background: transparent;
        cursor: pointer;
        font-size: 12px;
        line-height: 1;
        display: inline-flex;
        align-items: center;
        white-space: nowrap;
        color: inherit;
        min-width: 96px; /* reserve space so adjacent icons stay aligned */
    }

    .display.disabled {
        opacity: 0.6;
        cursor: default;
    }

    .display.bold {
        font-weight: 600;
    }

    .menu {
        position: absolute;
        top: 100%;
        background: #fff;
        border: 1px solid #e6eaee;
        border-radius: 4px;
        min-width: 160px;
        box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
        list-style: none;
        padding: 4px 0;
        z-index: 5;
    }

    .menu li {
        padding: 8px 10px;
        font-size: 12px;
        cursor: pointer;
    }

    .menu li:hover {
        background: #f5f7fa;
    }

    .dropdown-arrow {
        margin-left: 6px;
        font-size: 10px;
        line-height: 1;
        color: rgba(0,0,0,0.6);
        transition: transform 120ms ease;
        display: inline-block;
        width: 12px;
        text-align: center;
        user-select: none;
        pointer-events: none;
    }

    .dropdown-arrow.open {
        transform: rotate(180deg);
    }

    .menu li.disabled {
        opacity: 0.5;
        cursor: not-allowed;
        color: #8b96a3;
        user-select: none;
    }

    .menu li.disabled:hover {
        background: transparent;
    }

    .checkin-cell.open-up .menu {
        top: auto;
        bottom: calc(100% + 1px);
    }
</style>



// WEBPACK FOOTER //
// CheckinDropdown.vue?c85611a8